import {useState, useRef, useEffect, useMemo, useCallback} from "react";
import { motion, AnimatePresence } from 'motion/react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { ImageWrapper } from './ImageWrapper.jsx';

export function BannerCarousel({banners, openProductById, openCategoryById, getProductById, getCategoryById, categoriesPosition, logActivity}) {
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const _banners = useMemo(() => {
    return [...(banners || [])].sort((b1, b2) => {
      const b1Order = b1.order || Number.MAX_SAFE_INTEGER;
      const b2Order = b2.order || Number.MAX_SAFE_INTEGER;
      return b1Order - b2Order;
    });
  }, [banners]);
  const bannerIds = _banners.map((banner) => banner.id).join('|');
  // Entre el cambio de lista y el efecto que reinicia el índice hay un render con el valor viejo:
  // sin acotarlo, ese render buscaría una posición que ya no existe y no pintaría ningún banner.
  const safeIndex = currentBannerIndex < _banners.length ? currentBannerIndex : 0;
  const bannerTimerRef = useRef(null);
  const wasDragging = useRef(false);
  const slideAfter = 5000;

  const variants = {
    enter: (direction) => ({
      x: direction > 0 ? 1000 : -1000,
      opacity: 0,
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1,
    },
    exit: (direction) => ({
      zIndex: 0,
      x: direction < 0 ? 1000 : -1000,
      opacity: 0,
    }),
  };

  const stopBannerTimer = useCallback(() => {
    if (bannerTimerRef.current) {
      clearInterval(bannerTimerRef.current);
      bannerTimerRef.current = null;
    }
  }, []);

  const startBannerTimer = useCallback(() => {
    stopBannerTimer();
    if (_banners && _banners.length > 1) {
      // El intervalo sobrevive a los renders, así que no puede avanzar desde el índice de este: si
      // lo tomara del closure avanzaría siempre al mismo banner y el carrusel quedaría fijo ahí.
      // Con la forma funcional cada tick parte del valor vigente, venga de donde venga.
      bannerTimerRef.current = setInterval(() => {
        setDirection(1);
        setCurrentBannerIndex((previous) => (previous + 1 > _banners.length - 1 ? 0 : previous + 1));
      }, slideAfter);
    }
  }, [_banners, stopBannerTimer]);

  // Nota: cada ves que el estado o las propiedades cambian, las funciones se vuelven a crear, por eso se incluyen en las dependencias del useEffect.
  // Así useEffect siempre tendrá la versión más actualizada de las funciones y evitará problemas de sincronización. El useCallback le dice a React
  // que no vuelva a crear las funciones a menos que sus dependencias cambien, lo que ayuda a optimizar el rendimiento y evitar efectos secundarios
  // no deseados.

  useEffect(() => {
    startBannerTimer();
    return () => {
      stopBannerTimer();
    };
  }, [_banners, startBannerTimer, stopBannerTimer]);

  // El índice es estado del carrusel, pero quién arma la lista es la vista: al elegir una categoría
  // con otros banners hay que volver al primero. Se compara por ids y no por referencia para no
  // saltar al primero cuando dos vistas muestran exactamente los mismos banners.
  useEffect(() => {
    setCurrentBannerIndex(0);
  }, [bannerIds]);

  const goToPrevious = (event, log, entityId, action) => {
    if (event) event.stopPropagation();
    stopBannerTimer();
    setDirection(-1);
    const oldBanner = _banners[safeIndex];
    const newIndex = safeIndex - 1 < 0 ? _banners.length - 1 : safeIndex - 1;
    const newBanner = _banners[newIndex];
    if (newBanner && log) logActivity('banner-carousel', entityId, action, true, {old_banner_id: oldBanner.id, old_banner_name: oldBanner.name || null, new_banner_id: newBanner.id, new_banner_name: newBanner.name || null});
    setCurrentBannerIndex(newIndex);
    startBannerTimer();
  };

  const goToNext = (event, log, entityId, action) => {
    if (event) event.stopPropagation();
    stopBannerTimer();
    setDirection(1);
    const oldBanner = _banners[safeIndex];
    const newIndex = safeIndex + 1 > _banners.length - 1 ? 0 : safeIndex + 1;
    const newBanner = _banners[newIndex];
    if (newBanner && log) logActivity('banner-carousel', entityId, action, true, {old_banner_id: oldBanner.id, old_banner_name: oldBanner.name || null, new_banner_id: newBanner.id, new_banner_name: newBanner.name || null});
    setCurrentBannerIndex(newIndex);
    startBannerTimer();
  };

  const goToSlide = (event, index, log, entityId, action) => {
    if (event) event.stopPropagation();
    stopBannerTimer();
    setDirection(index > safeIndex ? 1 : -1);
    const oldBanner = _banners[safeIndex];
    const newBanner = _banners[index];
    if (newBanner && log) logActivity('banner-carousel', entityId, action, true, {old_banner_id: oldBanner.id, old_banner_name: oldBanner.name || null, new_banner_id: newBanner.id, new_banner_name: newBanner.name || null});
    setCurrentBannerIndex(index);
    startBannerTimer();
  };

  const handleClick = (banner) => {
    if (banner && banner.link_type === 'item' && openProductById) {
      openProductById(banner.link_target);
      const product = getProductById(banner.link_target);
      if (product) logActivity('banner-carousel', 'banner-image', 'click', true, {banner_id: banner.id, banner_name: banner.name || null, link_type: banner.link_type, link_target: banner.link_target, product_id: product.id, product_name: product.label || product.name || null});
    } else if (banner && banner.link_type === 'category' && openCategoryById) {
      openCategoryById(banner.link_target);
      const category = getCategoryById(banner.link_target);
      if (category) logActivity('banner-carousel', 'banner-image', 'click', true, {banner_id: banner.id, banner_name: banner.name || null, link_type: banner.link_type, link_target: banner.link_target, category_id: category.id, category_name: category.name || null});
    }
  };

  let currentBanner = _banners[safeIndex];

  if (currentBanner) {
    return (
      <div className={`w-full ${categoriesPosition === 'top' ? 'bg-mid' : ''}`}>
        <motion.div
          className={`w-full max-w-screen-xl mx-auto h-auto ${categoriesPosition === 'top' ? 'px-8' : 'px-4'} pt-8 ${currentBanner && currentBanner.link_type === 'item' ? 'cursor-pointer' : ''}`}
          initial={{opacity: 0, y: -20}}
          animate={{opacity: 1, y: 0}}
          transition={{duration: 0.5}}
        >
          <div className="relative w-full aspect-[3/1] rounded-2xl overflow-hidden group" onClick={() => { if (wasDragging.current) return; handleClick(currentBanner); }}>
            <AnimatePresence initial={false} custom={direction}>
              <motion.div
                key={safeIndex}
                custom={direction}
                variants={variants}
                initial="enter"
                animate="center"
                exit="exit"
                drag="x"
                dragConstraints={{ left: 0, right: 0 }}
                dragElastic={1}
                whileTap={{ scale: 0.8 }}
                whileHover={{ scale: 1.01 }}
                transition={{
                  x: { type: "spring", stiffness: 300, damping: 30 },
                  opacity: { duration: 0.2 }
                }}
                onDragStart={() => { wasDragging.current = true; }}
                onDragEnd={(e, {offset}) => {
                  const swipe = offset.x;
                  if (swipe < -100) {
                    goToNext(e, true, 'banner-image', 'swipe-left');
                  } else if (swipe > 100) {
                    goToPrevious(e, true, 'banner-image', 'swipe-right');
                  }
                  setTimeout(() => { wasDragging.current = false; }, 0);
                }}
                className="absolute inset-0"
              >
                <div className="w-full h-full object-cover" style={{backgroundColor: 'var(--playlist-item-background)'}}>
                  <ImageWrapper
                    image={currentBanner.image}
                    alt={`Banner promocional ${safeIndex + 1}`}
                    display="block"
                    width="100%"
                    height="100%"
                    backgroundColor="transparent"
                    margin="0"
                    padding="0"
                    borderRadius="0"
                    transition={true}
                    loadDelay={0.1}
                    onLoad={undefined}
                  />
                </div>
              </motion.div>
            </AnimatePresence>
            {_banners.length > 1 && (
              <div className="absolute bottom-4 flex z-10 w-full flex justify-center items-center">
                <div className="flex gap-2">
                  {_banners.map((_, index) => (
                    <button
                      key={index}
                      onClick={(event) => goToSlide(event, index, true, 'banner-indicator', 'click')}
                      className={`a11y-skip-target w-2 h-2 rounded-full transition-all cursor-pointer ${index === safeIndex ? 'bg-primary w-6' : 'bg-muted'}`}
                      aria-label={`Ir al banner ${index + 1}`}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    );
  } else return null;
}